import { useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { t } from '@/lib/i18n';
import type { Artifact } from '../schemas';
import styles from './DeliverableZone.module.css';

export interface DeliverableZoneProps {
  artifacts: Artifact[];
  emptyMessage?: string;
  onOpen: (artifact: Artifact) => void;
}

export function DeliverableZone({ artifacts, emptyMessage, onOpen }: DeliverableZoneProps) {
  if (artifacts.length === 0) {
    return <div className={styles.empty}>{emptyMessage ?? t('No deliverables yet.')}</div>;
  }
  return (
    <div className={styles.cards}>
      {artifacts.map((artifact) => (
        <DeliverableCard key={artifact.id} artifact={artifact} onOpen={() => onOpen(artifact)} />
      ))}
    </div>
  );
}

function DeliverableCard({ artifact, onOpen }: { artifact: Artifact; onOpen: () => void }) {
  const [copied, setCopied] = useState(false);
  const label = artifact.parameter_name ? humanizeName(artifact.parameter_name) : t('Output');

  const handleCopy = () => {
    void navigator.clipboard.writeText(artifact.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardIcon} aria-hidden="true">
          {mimeIcon(artifact.mime_type)}
        </span>
        <div className={styles.cardIdentity}>
          <span className={styles.cardTitle}>{label}</span>
          <span className={styles.cardMetaLine}>
            {mimeLabel(artifact.mime_type)} · {formatBytes(artifact.size_bytes)}
          </span>
        </div>
        <div className={styles.cardActions}>
          <button type="button" className={styles.actionBtn} onClick={handleCopy}>
            {copied ? t('Copied!') : t('Copy')}
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => downloadArtifact(artifact)}
          >
            {t('↓ Download')}
          </button>
          <button type="button" className={styles.actionBtnPrimary} onClick={onOpen}>
            {t('Open ↗')}
          </button>
        </div>
      </div>
      <button
        type="button"
        className={styles.cardPreview}
        onClick={onOpen}
        aria-label={t('Open {label}', { label })}
      >
        <ContentPreview artifact={artifact} />
        <span className={styles.previewFade} aria-hidden="true" />
      </button>
    </div>
  );
}

function ContentPreview({ artifact }: { artifact: Artifact }) {
  if (artifact.mime_type === 'text/markdown') {
    return <MarkdownPreview content={artifact.content} />;
  }
  if (artifact.mime_type === 'application/json') {
    return <JsonPreview content={artifact.content} />;
  }
  if (artifact.mime_type === 'text/csv') {
    return <CsvPreview content={artifact.content} />;
  }
  if (artifact.mime_type === 'text/html') {
    return (
      <div className={styles.htmlPreview}>
        <span className={styles.htmlPreviewIcon} aria-hidden="true">
          🌐
        </span>
        <span>{t('HTML document — click to open in a new window')}</span>
      </div>
    );
  }
  return <pre className={styles.previewPre}>{artifact.content.slice(0, 600)}</pre>;
}

function MarkdownPreview({ content }: { content: string }) {
  const html = useMemo(() => {
    const rendered = marked.parse(content.slice(0, 1000), { async: false });
    return DOMPurify.sanitize(rendered);
  }, [content]);
  return <div className={styles.previewMarkdown} dangerouslySetInnerHTML={{ __html: html }} />;
}

function JsonPreview({ content }: { content: string }) {
  const preview = useMemo(() => {
    try {
      const pretty = JSON.stringify(JSON.parse(content), null, 2);
      return pretty.split('\n').slice(0, 25).join('\n');
    } catch {
      return content.slice(0, 600);
    }
  }, [content]);
  return <pre className={styles.previewPre}>{preview}</pre>;
}

function CsvPreview({ content }: { content: string }) {
  // Simple split preview — quoted fields with embedded commas show as split
  // cells here, which is acceptable for a truncated preview. Full RFC-4180
  // parsing happens in ArtifactView when the user opens the file.
  const rows = useMemo(
    () =>
      content
        .split('\n')
        .slice(0, 5)
        .filter(Boolean)
        .map((r) => r.split(',').map((c) => c.replace(/^"|"$/g, '').trim())),
    [content],
  );
  const [header, ...body] = rows;
  if (!header) return <pre className={styles.previewPre}>{content.slice(0, 300)}</pre>;
  return (
    <div className={styles.previewTableWrap}>
      <table className={styles.previewTable}>
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th key={i}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Utilities ──────────────────────────────────────────────────────────

function humanizeName(name: string): string {
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function mimeIcon(mime: string): string {
  if (mime === 'text/markdown') return '📝';
  if (mime === 'application/json') return '📋';
  if (mime === 'text/csv') return '📊';
  if (mime === 'text/html') return '🌐';
  return '📄';
}

function mimeLabel(mime: string): string {
  if (mime === 'text/markdown') return 'Markdown';
  if (mime === 'application/json') return 'JSON';
  if (mime === 'text/csv') return 'CSV';
  if (mime === 'text/html') return 'HTML';
  if (mime === 'text/plain') return 'Plain text';
  return mime;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadArtifact(artifact: Artifact): void {
  const base = sanitizeFilename(artifact.parameter_name ?? `output-${artifact.id.slice(0, 8)}`);
  const ext = mimeToExtension(artifact.mime_type);
  const blob = new Blob([artifact.content], { type: `${artifact.mime_type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function sanitizeFilename(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'download'
  );
}

function mimeToExtension(mime: string): string {
  if (mime === 'text/markdown') return 'md';
  if (mime === 'application/json') return 'json';
  if (mime === 'text/html') return 'html';
  if (mime === 'text/csv') return 'csv';
  if (mime === 'text/plain') return 'txt';
  if (mime.startsWith('text/')) return 'txt';
  return 'bin';
}
